import React, { useState, useEffect } from 'react';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CategoryTreeNode from './components/CategoryTreeNode';
import ServiceEditPanel from './components/ServiceEditPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import ValidationTestPanel from './components/ValidationTestPanel';
import SearchFilterBar from './components/SearchFilterBar';
import ServiceZoneManager from './components/ServiceZoneManager';

const ServiceCatalogManager = () => {
  const [catalogData, setCatalogData] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [draggedNode, setDraggedNode] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [showBulkPanel, setShowBulkPanel] = useState(false);
  const [showValidationPanel, setShowValidationPanel] = useState(false);
  const [activeTab, setActiveTab] = useState('catalog');

  useEffect(() => {
    const mockCatalog = [
      {
        id: 'cat-1',
        name: 'Plomberie générale',
        type: 'category',
        active: true,
        children: [
          {
            id: 'eq-1',
            name: 'Robinetterie',
            type: 'equipment',
            active: true,
            children: [
              {
                id: 'prob-1',
                name: 'Fuite',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-1',
                    name: 'Réparation fuite robinet',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '85',
                    vatRate: '20',
                    warrantyMonths: '6',
                    estimatedDuration: '45',
                    description: 'Intervention pour réparer une fuite sur robinetterie standard. Inclut le diagnostic, le remplacement des joints défectueux et la vérification de l\'étanchéité.',
                    skillRequirements: ['plumbing_basic'],
                    allowFieldAmendments: true,
                    customerValidationRequired: false,
                    active: true
                  },
                  {
                    id: 'serv-2',
                    name: 'Remplacement robinet complet',
                    type: 'service',
                    pricingModel: 'quote',
                    estimatedDuration: '90',
                    description: 'Remplacement complet d\'un robinet. Prix sur devis selon le modèle choisi par le client.',
                    skillRequirements: ['plumbing_basic', 'plumbing_advanced'],
                    allowFieldAmendments: true,
                    customerValidationRequired: true,
                    active: true
                  }
                ]
              },
              {
                id: 'prob-2',
                name: 'Débit faible',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-3',
                    name: 'Détartrage robinet',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '65',
                    vatRate: '20',
                    warrantyMonths: '3',
                    estimatedDuration: '30',
                    description: 'Nettoyage et détartrage du mousseur et des composants internes du robinet.',
                    skillRequirements: ['plumbing_basic'],
                    allowFieldAmendments: false,
                    customerValidationRequired: false,
                    active: true
                  }
                ]
              }
            ]
          },
          {
            id: 'eq-2',
            name: 'Toilettes',
            type: 'equipment',
            active: true,
            children: [
              {
                id: 'prob-3',
                name: 'Fuite chasse d\'eau',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-4',
                    name: 'Réparation mécanisme chasse',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '95',
                    vatRate: '10',
                    warrantyMonths: '6',
                    estimatedDuration: '60',
                    description: 'Réparation ou remplacement du mécanisme de chasse d\'eau. Inclut le diagnostic, les pièces standard et la mise en service.',
                    skillRequirements: ['plumbing_basic'],
                    allowFieldAmendments: true,
                    customerValidationRequired: false,
                    active: true
                  }
                ]
              },
              {
                id: 'prob-4',
                name: 'WC bouché',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-5',
                    name: 'Débouchage WC simple',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '120',
                    vatRate: '20',
                    warrantyMonths: '1',
                    estimatedDuration: '45',
                    description: 'Débouchage de toilettes avec équipement standard (ventouse, furet manuel).',
                    skillRequirements: ['plumbing_basic'],
                    allowFieldAmendments: false,
                    customerValidationRequired: false,
                    active: true
                  },
                  {
                    id: 'serv-6',
                    name: 'Débouchage WC haute pression',
                    type: 'service',
                    pricingModel: 'quote',
                    estimatedDuration: '120',
                    description: 'Débouchage avec équipement haute pression pour obstructions importantes. Prix sur devis selon la complexité.',
                    skillRequirements: ['plumbing_advanced', 'drainage'],
                    allowFieldAmendments: true,
                    customerValidationRequired: true,
                    active: true
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 'cat-2',
        name: 'Chauffage',
        type: 'category',
        active: true,
        children: [
          {
            id: 'eq-3',
            name: 'Chaudière',
            type: 'equipment',
            active: true,
            children: [
              {
                id: 'prob-5',
                name: 'Panne',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-7',
                    name: 'Diagnostic panne chaudière',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '150',
                    vatRate: '20',
                    warrantyMonths: '0',
                    estimatedDuration: '90',
                    description: 'Diagnostic complet de panne de chaudière avec rapport détaillé. Le devis de réparation sera établi après diagnostic.',
                    skillRequirements: ['heating', 'gas_certified'],
                    allowFieldAmendments: false,
                    customerValidationRequired: true,
                    active: true
                  }
                ]
              },
              {
                id: 'prob-6',
                name: 'Entretien annuel',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-8',
                    name: 'Entretien chaudière gaz',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '135',
                    vatRate: '10',
                    warrantyMonths: '12',
                    estimatedDuration: '75',
                    description: 'Entretien annuel obligatoire de chaudière gaz. Inclut nettoyage, réglages, contrôle des émissions et attestation d\'entretien.',
                    skillRequirements: ['heating', 'gas_certified'],
                    allowFieldAmendments: false,
                    customerValidationRequired: false,
                    active: true
                  }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 'cat-3',
        name: 'Urgences',
        type: 'category',
        active: true,
        children: [
          {
            id: 'eq-4',
            name: 'Intervention urgente',
            type: 'equipment',
            active: true,
            children: [
              {
                id: 'prob-7',
                name: 'Fuite importante',
                type: 'problem',
                active: true,
                children: [
                  {
                    id: 'serv-9',
                    name: 'Intervention urgence fuite',
                    type: 'service',
                    pricingModel: 'fixed',
                    basePrice: '180',
                    vatRate: '20',
                    warrantyMonths: '3',
                    estimatedDuration: '60',
                    description: 'Intervention d\'urgence pour arrêter une fuite importante. Disponible 24h/24. Tarif majoré hors heures ouvrables.',
                    skillRequirements: ['plumbing_advanced', 'emergency'],
                    allowFieldAmendments: true,
                    customerValidationRequired: false,
                    seasonalPricing: true,
                    active: true
                  }
                ]
              }
            ]
          }
        ]
      }
    ];

    setCatalogData(mockCatalog);
  }, []);

  const handleNodeSelect = (node) => {
    setSelectedNode(node);
  };

  const handleDragStart = (node) => {
    setDraggedNode(node);
  };

  const handleDrop = (targetNode) => {
    if (draggedNode && draggedNode?.id !== targetNode?.id) {
      console.log('Moving', draggedNode?.name, 'to', targetNode?.name);
      alert(`Déplacement de "${draggedNode?.name}" vers "${targetNode?.name}"\n\nCette fonctionnalité sera implémentée avec la validation de la hiérarchie.`);
    }
    setDraggedNode(null);
  };

  const handleSave = (updatedNode) => {
    console.log('Saving node:', updatedNode);
    alert(`Modifications enregistrées pour "${updatedNode?.name}"\n\nLes changements sont maintenant visibles dans toutes les interfaces.`);
    setSelectedNode(null);
  };

  const handleCancel = () => {
    setSelectedNode(null);
  };

  const handleEdit = (node) => {
    setSelectedNode(node);
  };

  const handleDelete = (node) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer "${node?.name}" ?\n\nCette action est irréversible.`)) {
      console.log('Deleting node:', node);
      alert(`"${node?.name}" a été supprimé du catalogue.`);
    }
  };

  const handleAddChild = (parentNode) => {
    const childTypes = {
      category: 'equipment',
      equipment: 'problem',
      problem: 'service'
    };

    const newChildType = childTypes?.[parentNode?.type];
    if (!newChildType) {
      alert('Impossible d\'ajouter un élément enfant à ce type.');
      return;
    }

    const newNode = {
      id: `new-${Date.now()}`,
      name: `Nouveau ${newChildType}`,
      type: newChildType,
      active: true,
      parentId: parentNode?.id
    };

    setSelectedNode(newNode);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  const handleFilter = (newFilters) => {
    setFilters(newFilters);
  };

  const handleImport = (result) => {
    alert(`Import réussi!\n\n${result?.recordsImported} enregistrements importés depuis ${result?.fileName}`);
  };

  const handleBulkEdit = (operation) => {
    alert(`Opération en masse terminée!\n\nType: ${operation?.operation}\n${operation?.affectedServices} services modifiés`);
  };

  const handleLogout = () => {
    console.log('Logging out...');
  };

  const countTotalNodes = (nodes) => {
    let count = 0;
    const traverse = (items) => {
      items?.forEach(item => {
        count++;
        if (item?.children) {
          traverse(item?.children);
        }
      });
    };
    traverse(nodes);
    return count;
  };

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedHeader />
      <QuickActionToolbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-6">
          <div className="flex gap-4 border-b border-border">
            <button
              onClick={() => setActiveTab('catalog')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'catalog' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Catalogue de services
            </button>
            <button
              onClick={() => setActiveTab('zones')}
              className={`px-4 py-2 font-medium transition-colors ${
                activeTab === 'zones' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
              }`}
            >
              Zones d'intervention
            </button>
          </div>
        </div>

        {activeTab === 'zones' ? (
          <ServiceZoneManager />
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-foreground mb-2">
                  Gestionnaire de catalogue
                </h1>
                <p className="text-muted-foreground">
                  Configuration des services, tarifs et hiérarchie du catalogue
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  iconName="Layers"
                  onClick={() => setShowBulkPanel(!showBulkPanel)}
                >
                  Opérations
                </Button>
                <Button
                  variant="outline"
                  iconName="TestTube"
                  onClick={() => setShowValidationPanel(!showValidationPanel)}
                >
                  Validation
                </Button>
                <Button
                  iconName="Plus"
                  onClick={() => handleAddChild({ id: 'root', type: 'root' })}
                >
                  Nouvelle catégorie
                </Button>
              </div>
            </div>

            <SearchFilterBar
              onSearch={handleSearch}
              onFilter={handleFilter}
              totalItems={countTotalNodes(catalogData)}
            />

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">

            <div className="lg:col-span-4 xl:col-span-3">
              <div className="bg-card rounded-lg border border-border overflow-hidden">
                <div className="p-4 border-b border-border bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="FolderTree" size={20} className="text-primary" />
                    </div>
                    <div>
                      <h2 className="text-base font-semibold text-foreground">
                        Arborescence
                      </h2>
                      <p className="text-xs text-muted-foreground">
                        Glisser-déposer pour réorganiser
                      </p>
                    </div>
                  </div>
                </div>

                <div className="p-4 max-h-[calc(100vh-320px)] overflow-y-auto scrollbar-thin">
                  <div className="space-y-1 group">
                    {catalogData?.map((node) => (
                      <CategoryTreeNode
                        key={node?.id}
                        node={node}
                        onSelect={handleNodeSelect}
                        selectedId={selectedNode?.id}
                        onDragStart={handleDragStart}
                        onDrop={handleDrop}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onAddChild={handleAddChild}
                      />
                    ))}
                  </div>
                </div>
              </div>

              {showBulkPanel && (
                <div className="mt-6 animate-slide-up">
                  <BulkOperationsPanel
                    onImport={handleImport}
                    onExport={() => console.log('Export')}
                    onBulkEdit={handleBulkEdit}
                  />
                </div>
              )}
            </div>

            <div className="lg:col-span-8 xl:col-span-9">
              <div className="bg-card rounded-lg border border-border h-[calc(100vh-280px)] overflow-hidden">
                <ServiceEditPanel
                  selectedNode={selectedNode}
                  onSave={handleSave}
                  onCancel={handleCancel}
                />
              </div>

              {showValidationPanel && (
                <div className="mt-6 animate-slide-up">
                  <ValidationTestPanel catalogData={catalogData} />
                </div>
              )}
            </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Icon name="Package" size={20} className="text-blue-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">156</div>
                    <div className="text-xs text-muted-foreground">Services actifs</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                    <Icon name="DollarSign" size={20} className="text-green-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">89</div>
                    <div className="text-xs text-muted-foreground">Prix fixes</div>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-lg border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Icon name="FileText" size={20} className="text-purple-600" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-foreground">67</div>
                    <div className="text-xs text-muted-foreground">Sur devis</div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceCatalogManager;